import { useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, Pencil, Palette, Layers, Sparkles, Upload, FileText, Film, Image as ImageIcon, X, Download,
} from "lucide-react";

const ALL = "__all__";
const ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.mp4,.mov,.webm,.avi,.m4v";
const COVER_ACCEPT = ".jpg,.jpeg,.png,.webp";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function detectKind(contentType = "", filename = "") {
  const ct = (contentType || "").toLowerCase();
  const ext = filename.includes(".") ? filename.split(".").pop().toLowerCase() : "";
  if (ct.startsWith("image/") || ["jpg", "jpeg", "png", "webp", "gif", "heic"].includes(ext)) return "image";
  if (ct.startsWith("video/") || ["mp4", "mov", "webm", "avi", "m4v"].includes(ext)) return "video";
  if (ct === "application/pdf" || ext === "pdf") return "pdf";
  if (["doc", "docx"].includes(ext) || ct.includes("word") || ct.includes("msword")) return "doc";
  return "file";
}

function mediaUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("blob:")) return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  const token = localStorage.getItem("renka_token");
  const withHost = `${BACKEND}${path}`;
  if (!token || !path.startsWith("/api/files/")) return withHost;
  const join = withHost.includes("?") ? "&" : "?";
  return `${withHost}${join}auth=${encodeURIComponent(token)}`;
}

async function downloadMedia(file) {
  try {
    const url = mediaUrl(file.url);
    const res = await fetch(url);
    if (!res.ok) throw new Error("Dosya alınamadı");
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = file.original_filename || "dosya";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch (e) {
    toast.error(e.message || "İndirme başarısız");
  }
}

function FileKindIcon({ kind, className = "h-5 w-5" }) {
  if (kind === "image") return <ImageIcon className={className} />;
  if (kind === "video") return <Film className={className} />;
  return <FileText className={className} />;
}

function ActivityCover({ activity, hex }) {
  const files = activity.files || [];
  const coverUrl = activity.cover_url || files.find((f) => f.kind === "image")?.url;
  const coverFile = files.find((f) => f.url === coverUrl) || files.find((f) => f.kind === "image");
  const preview = coverFile || files[0];

  if (coverUrl) {
    return (
      <div className="relative h-40 w-full overflow-hidden bg-slate-100">
        <img
          src={mediaUrl(coverUrl)}
          alt=""
          className="h-full w-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling?.classList.remove("hidden"); }}
        />
        <div className="hidden absolute inset-0 flex items-center justify-center" style={{ backgroundColor: `${hex}18`, color: hex }}>
          <ImageIcon className="h-10 w-10 opacity-60" />
        </div>
      </div>
    );
  }

  if (preview?.kind === "video") {
    return (
      <div className="h-40 w-full overflow-hidden bg-slate-900">
        <video src={mediaUrl(preview.url)} className="h-full w-full object-cover" preload="metadata" muted />
      </div>
    );
  }

  const label = preview?.kind === "pdf" ? "PDF" : preview?.kind === "doc" ? "Word" : "Dosya";
  return (
    <div className="flex h-36 w-full flex-col items-center justify-center gap-2" style={{ background: `linear-gradient(135deg, ${hex}22, ${hex}08)`, color: hex }}>
      <FileKindIcon kind={preview?.kind} className="h-10 w-10 opacity-80" />
      <span className="text-xs font-semibold uppercase tracking-wide opacity-80">{preview ? label : "Kapak yok"}</span>
      {preview?.original_filename && (
        <span className="max-w-[80%] truncate px-3 text-[11px] text-slate-500">{preview.original_filename}</span>
      )}
    </div>
  );
}

function ActivityCategoryManager({ categories, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: "#7C3AED" });
  const [saving, setSaving] = useState(false);
  const [forceDel, setForceDel] = useState(null);

  const openCreate = () => { setEditing(null); setForm({ name: "", color: "#7C3AED" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, color: c.color || "#7C3AED" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Kategori adı zorunlu"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/activity-categories/${editing.id}`, form);
        toast.success("Kategori güncellendi");
      } else {
        await api.post("/activity-categories", form);
        toast.success("Kategori eklendi");
      }
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (cat, force) => {
    try {
      await api.delete(`/activity-categories/${cat.id}${force ? "?force=true" : ""}`);
      toast.success("Kategori silindi");
      setForceDel(null);
      reload();
    } catch (err) {
      if (err.response?.status === 409) {
        setForceDel({ cat, detail: formatApiErrorDetail(err.response?.data?.detail) });
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    }
  };

  return (
    <div data-testid="activity-category-manager">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">Etkinlik kategorilerini yönetin. Ad değişikliği mevcut etkinliklere yansır.</p>
        <Button onClick={openCreate} data-testid="add-activity-category-button" className="h-11 rounded-xl bg-violet-600 font-semibold text-white hover:bg-violet-700">
          <Plus className="mr-1.5 h-4 w-4" /> Kategori Ekle
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {categories.map((c) => (
          <Card key={c.id} data-testid={`activity-category-card-${c.id}`} className="flex items-center gap-3 rounded-2xl border-slate-200/60 p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <span className="h-8 w-8 flex-shrink-0 rounded-xl" style={{ backgroundColor: c.color }} />
            <span className="min-w-0 flex-1 truncate font-heading font-semibold text-slate-900">{c.name}</span>
            <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-activity-category-${c.id}`} className="h-8 w-8 rounded-lg"><Pencil className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" onClick={() => doDelete(c, false)} data-testid={`delete-activity-category-${c.id}`} className="h-8 w-8 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
          </Card>
        ))}
        {categories.length === 0 && (
          <Card className="col-span-full rounded-2xl border-dashed p-14 text-center text-slate-400">
            <Layers className="mx-auto mb-3 h-9 w-9 text-slate-300" />Henüz kategori yok. Önce kategori ekleyin.
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="activity-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Kategori Düzenle" : "Yeni Kategori"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Kategori Adı *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="activity-category-name-input" placeholder="Örn: Grup Etkinlikleri" />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Palette className="h-4 w-4 text-violet-500" /> Renk</Label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="activity-category-color-input" className="h-10 w-16 cursor-pointer rounded-lg border border-slate-200" />
                <span className="text-sm text-slate-500">{form.color}</span>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">İptal</Button>
              <Button type="submit" disabled={saving} data-testid="activity-category-save-button" className="rounded-xl bg-violet-600 text-white hover:bg-violet-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!forceDel} onOpenChange={(o) => !o && setForceDel(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="force-delete-activity-category-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kategoriyi etkinlikleriyle sil?</AlertDialogTitle>
            <AlertDialogDescription>
              {forceDel?.detail || "Bu kategoride etkinlikler var."} Onaylarsanız kategori ve bağlı tüm etkinlikler silinecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={() => doDelete(forceDel.cat, true)} data-testid="confirm-force-delete-activity-category" className="rounded-xl bg-red-600 hover:bg-red-700">
              Evet, Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const emptyForm = () => ({
  title: "",
  category: "",
  note: "",
  cover_url: "",
  cover_path: "",
  cover_content_type: "",
  files: [],
});

export default function ActivityPool() {
  const fileRef = useRef(null);
  const coverRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(ALL);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [del, setDel] = useState(null);

  const colorOf = (name) => categories.find((c) => c.name === name)?.color || "#7C3AED";

  const loadCategories = async () => {
    const { data } = await api.get("/activity-categories");
    setCategories(data);
  };

  const load = async () => {
    try {
      const [c, a] = await Promise.all([api.get("/activity-categories"), api.get("/activity-pool")]);
      setCategories(c.data);
      setItems(a.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (filter === ALL ? items : items.filter((x) => x.category === filter)),
    [items, filter]
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), category: categories[0]?.name || "" });
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      title: a.title || "",
      category: a.category || "",
      note: a.note || "",
      cover_url: a.cover_url || "",
      cover_path: a.cover_path || "",
      cover_content_type: a.cover_content_type || "",
      files: a.files || [],
    });
    setOpen(true);
  };

  const uploadOne = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return {
      url: data.url,
      path: data.path,
      content_type: data.content_type,
      original_filename: data.original_filename || f.name,
      kind: detectKind(data.content_type, data.original_filename || f.name),
    };
  };

  const onPickFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of list) uploaded.push(await uploadOne(f));
      setForm((prev) => {
        const files = [...prev.files, ...uploaded];
        const next = { ...prev, files };
        if (!prev.cover_url) {
          const img = uploaded.find((x) => x.kind === "image") || files.find((x) => x.kind === "image");
          if (img) {
            next.cover_url = img.url;
            next.cover_path = img.path;
            next.cover_content_type = img.content_type;
          }
        }
        return next;
      });
      toast.success(`${uploaded.length} dosya yüklendi`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const onPickCover = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const uploaded = await uploadOne(f);
      if (uploaded.kind !== "image") {
        toast.error("Kapak için görsel seçin (JPG, PNG, WEBP)");
        return;
      }
      setForm((prev) => ({
        ...prev,
        cover_url: uploaded.url,
        cover_path: uploaded.path,
        cover_content_type: uploaded.content_type,
      }));
      toast.success("Kapak görseli yüklendi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (idx) => {
    setForm((prev) => {
      const removed = prev.files[idx];
      const files = prev.files.filter((_, i) => i !== idx);
      const next = { ...prev, files };
      if (removed && prev.cover_url === removed.url) {
        const img = files.find((x) => x.kind === "image");
        next.cover_url = img?.url || "";
        next.cover_path = img?.path || "";
        next.cover_content_type = img?.content_type || "";
      }
      return next;
    });
  };

  const clearCover = () => {
    setForm((prev) => ({ ...prev, cover_url: "", cover_path: "", cover_content_type: "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Başlık zorunlu"); return; }
    if (!form.category) { toast.error("Kategori seçin"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        category: form.category,
        note: form.note,
        cover_url: form.cover_url || null,
        cover_path: form.cover_path || null,
        cover_content_type: form.cover_content_type || null,
        files: form.files,
      };
      if (editing) {
        await api.put(`/activity-pool/${editing.id}`, payload);
        toast.success("Etkinlik güncellendi");
      } else {
        await api.post("/activity-pool", payload);
        toast.success("Etkinlik eklendi");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDel = async () => {
    try {
      await api.delete(`/activity-pool/${del.id}`);
      toast.success("Etkinlik silindi");
      setDel(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const reloadAll = async () => {
    await loadCategories();
    const { data } = await api.get("/activity-pool");
    setItems(data);
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="activity-pool-page">
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">Etkinlik Havuzu</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">Etkinlik Havuzu</h1>
          <p className="mt-2 text-sm text-slate-600">Etkinlik materyallerini kategorilere göre havuzda saklayın ve yönetin.</p>
        </header>

        <Tabs defaultValue="etkinlik" className="w-full">
          <TabsList className="mb-6 rounded-xl bg-slate-100">
            <TabsTrigger value="etkinlik" data-testid="tab-activity-pool" className="rounded-lg data-[state=active]:bg-white">Etkinlik</TabsTrigger>
            <TabsTrigger value="kategoriler" data-testid="tab-activity-categories" className="rounded-lg data-[state=active]:bg-white">Kategori</TabsTrigger>
          </TabsList>

          <TabsContent value="etkinlik">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
              <p className="text-sm text-slate-600">PDF, Word, görsel ve video dosyalarıyla etkinlik ekleyin.</p>
              <Button onClick={openCreate} data-testid="add-activity-button" className="h-11 rounded-xl bg-violet-600 font-semibold text-white hover:bg-violet-700">
                <Plus className="mr-1.5 h-4 w-4" /> Etkinlik Ekle
              </Button>
            </div>

            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
            ) : (
              <>
                <div className="mb-6 flex flex-wrap gap-2">
                  {[[ALL, "Tümü"], ...categories.map((c) => [c.name, c.name])].map(([k, label]) => {
                    const active = filter === k;
                    const c = k === ALL ? "#7C3AED" : colorOf(k);
                    return (
                      <button
                        key={k}
                        onClick={() => setFilter(k)}
                        data-testid={`activity-filter-${k}`}
                        className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${active ? "text-white" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                        style={active ? { backgroundColor: c, borderColor: c } : {}}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                {filtered.length === 0 ? (
                  <Card className="rounded-2xl border-dashed p-16 text-center text-slate-500" data-testid="activity-pool-empty">
                    <Sparkles className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                    Henüz etkinlik yok. “Etkinlik Ekle” ile başla.
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {filtered.map((a) => {
                      const hex = colorOf(a.category);
                      const files = a.files || [];
                      return (
                        <Card key={a.id} data-testid={`activity-card-${a.id}`} className="overflow-hidden rounded-2xl border-slate-200/60 p-0 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" style={{ borderTop: `4px solid ${hex}` }}>
                          <ActivityCover activity={a} hex={hex} />
                          <div className="p-5">
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                              <Badge className="rounded-full border-0 text-white hover:opacity-90" style={{ backgroundColor: hex }}>{a.category}</Badge>
                              <span className="text-xs text-slate-400">{files.length} dosya</span>
                            </div>
                            <h3 className="font-heading text-base font-semibold text-slate-900">{a.title}</h3>
                            {a.note && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{a.note}</p>}
                            {files.length > 0 && (
                              <div className="mt-3 space-y-1.5">
                                {files.map((f, i) => (
                                  <button
                                    key={`${a.id}-f-${i}`}
                                    type="button"
                                    onClick={() => downloadMedia(f)}
                                    className="flex w-full items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-left text-xs text-slate-600 hover:bg-slate-100"
                                  >
                                    <FileKindIcon kind={f.kind} className="h-3.5 w-3.5 text-slate-400" />
                                    <span className="min-w-0 flex-1 truncate">{f.original_filename || "Dosya"}</span>
                                    <Download className="h-3.5 w-3.5 text-slate-400" />
                                  </button>
                                ))}
                              </div>
                            )}
                            <div className="mt-4 flex justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`edit-activity-${a.id}`} className="h-9 w-9 rounded-lg">
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => setDel(a)} data-testid={`delete-activity-${a.id}`} className="h-9 w-9 rounded-lg text-red-600 hover:bg-red-50">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="kategoriler">
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
            ) : (
              <ActivityCategoryManager categories={categories} reload={reloadAll} />
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="max-h-[92vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="activity-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Etkinliği Düzenle" : "Yeni Etkinlik"}</DialogTitle>
            <DialogDescription>Kapak görseli, kategori, not ve dosyaları yönetin.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Başlık *</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="activity-title-input" placeholder="Etkinlik adı" />
            </div>
            <div className="space-y-2">
              <Label>Kategori *</Label>
              <Select value={form.category || undefined} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="activity-category-select"><SelectValue placeholder="Kategori seç" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {categories.length === 0 && <p className="text-xs text-amber-600">Önce Kategori sekmesinden kategori ekleyin.</p>}
            </div>
            <div className="space-y-2">
              <Label>Not (opsiyonel)</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="activity-note-input" placeholder="Etkinlik açıklaması / içerik notu" />
            </div>

            <div className="space-y-2">
              <Label>Kapak görseli</Label>
              <input ref={coverRef} type="file" accept={COVER_ACCEPT} onChange={onPickCover} className="hidden" data-testid="activity-cover-input" />
              {form.cover_url ? (
                <div className="relative overflow-hidden rounded-xl border border-slate-200">
                  <img src={mediaUrl(form.cover_url)} alt="Kapak" className="h-36 w-full object-cover" />
                  <div className="absolute right-2 top-2 flex gap-1">
                    <Button type="button" size="sm" variant="secondary" className="h-8 rounded-lg" onClick={() => coverRef.current?.click()}>Değiştir</Button>
                    <Button type="button" size="icon" variant="secondary" className="h-8 w-8 rounded-lg" onClick={clearCover}><X className="h-4 w-4" /></Button>
                  </div>
                </div>
              ) : (
                <Button type="button" variant="outline" disabled={uploading} onClick={() => coverRef.current?.click()} data-testid="activity-cover-button" className="h-11 w-full rounded-xl">
                  <ImageIcon className="mr-2 h-4 w-4" /> Kapak Görseli Yükle
                </Button>
              )}
            </div>

            <div className="space-y-2">
              <Label>Dosyalar</Label>
              <input ref={fileRef} type="file" multiple accept={ACCEPT} onChange={onPickFiles} className="hidden" data-testid="activity-file-input" />
              <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="activity-upload-button" className="h-11 w-full rounded-xl">
                {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                Dosya Yükle (PDF, Word, JPG, PNG, WEBP, Video)
              </Button>
              {form.files.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  {form.files.map((f, i) => (
                    <div key={`new-f-${i}`} className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm">
                      <FileKindIcon kind={f.kind} className="h-4 w-4 text-slate-400" />
                      <span className="min-w-0 flex-1 truncate text-slate-700">{f.original_filename || "Dosya"}</span>
                      {f.kind === "image" && (
                        <button
                          type="button"
                          className="text-xs font-medium text-violet-600 hover:underline"
                          onClick={() => setForm((prev) => ({
                            ...prev,
                            cover_url: f.url,
                            cover_path: f.path,
                            cover_content_type: f.content_type,
                          }))}
                        >
                          Kapak yap
                        </button>
                      )}
                      <button type="button" onClick={() => removeFile(i)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">İptal</Button>
              <Button type="submit" disabled={saving || uploading || !categories.length} data-testid="activity-save-button" className="rounded-xl bg-violet-600 text-white hover:bg-violet-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Güncelle" : "Kaydet")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-activity-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Etkinliği sil?</AlertDialogTitle>
            <AlertDialogDescription><strong>{del?.title}</strong> havuzdan silinecek.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDel} data-testid="confirm-delete-activity" className="rounded-xl bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
